import React from 'react';
import { Progress, Card, Typography, Space, Tag, Statistic } from 'antd';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { ProgressBarProps } from '../types';
import { PerformanceUtils } from '../utils/performanceUtils';
import './ProgressBar.css';

const { Text, Title } = Typography;

export const ProgressBar: React.FC<ProgressBarProps> = ({
  progress,
  showDetail = true,
  compact = false
}) => {
  if (!progress) {
    return null;
  }

  const {
    fileName,
    currentStep,
    progress: fileProgress,
    overallProgress,
    currentFileIndex,
    totalFiles,
    estimatedTimeRemaining,
    averageProcessingTime
  } = progress;

  // 格式化时间
  const formatTime = (seconds?: number) => {
    if (!seconds || seconds <= 0) return '计算中...';
    return PerformanceUtils.formatDuration(seconds * 1000);
  };

  // 获取整体状态
  const getOverallStatus = () => {
    if (overallProgress >= 100) {
      return {
        status: 'success' as const,
        icon: <CheckCircleOutlined />,
        text: '处理完成',
        color: '#52c41a'
      };
    } else if (overallProgress > 0) {
      return {
        status: 'active' as const,
        icon: <LoadingOutlined />,
        text: '正在处理',
        color: '#1890ff'
      };
    } else {
      return {
        status: 'normal' as const,
        icon: <ClockCircleOutlined />,
        text: '准备中',
        color: '#faad14'
      };
    }
  };

  const statusInfo = getOverallStatus();

  if (compact) {
    return (
      <div className="progress-bar-compact">
        <div className="compact-header">
          <Space size="small">
            {statusInfo.icon}
            <Text strong>{statusInfo.text}</Text>
            <Text type="secondary">
              {currentFileIndex + 1}/{totalFiles}
            </Text>
          </Space>
          <Text type="secondary">{Math.round(overallProgress)}%</Text>
        </div>

        <Progress
          percent={overallProgress}
          status={statusInfo.status}
          showInfo={false}
          strokeColor={statusInfo.color}
          size="small"
        />

        {showDetail && (
          <div className="compact-detail">
            <Text type="secondary" style={{ fontSize: 12 }}>
              当前: {fileName} - {currentStep}
            </Text>
          </div>
        )}
      </div>
    );
  }

  return (
    <Card className="progress-bar-container" size="small">
      {/* 整体进度 */}
      <div className="overall-progress">
        <div className="progress-header">
          <Space>
            <span style={{ color: statusInfo.color }}>
              {statusInfo.icon}
            </span>
            <Title level={5} style={{ margin: 0 }}>
              {statusInfo.text}
            </Title>
            <Tag color={statusInfo.status === 'success' ? 'green' :
                      statusInfo.status === 'active' ? 'blue' : 'orange'}>
              {currentFileIndex + 1}/{totalFiles}
            </Tag>
          </Space>

          <Text strong style={{ fontSize: 16 }}>
            {Math.round(overallProgress)}%
          </Text>
        </div>

        <Progress
          percent={overallProgress}
          status={statusInfo.status}
          strokeColor={{
            '0%': statusInfo.color,
            '100%': statusInfo.status === 'success' ? '#52c41a' : statusInfo.color,
          }}
          strokeWidth={8}
          showInfo={false}
        />
      </div>

      {showDetail && (
        <>
          {/* 当前文件进度 */}
          <div className="current-file-progress">
            <div className="current-file-header">
              <Text type="secondary">当前文件:</Text>
              <Text strong ellipsis style={{ maxWidth: 300 }}>
                {fileName}
              </Text>
            </div>

            <div className="current-step">
              <Space size="small">
                <LoadingOutlined spin style={{ color: '#1890ff' }} />
                <Text>{currentStep}</Text>
                <Text type="secondary">({Math.round(fileProgress)}%)</Text>
              </Space>
            </div>

            <Progress
              percent={fileProgress}
              size="small"
              strokeColor="#1890ff"
              showInfo={false}
            />
          </div>

          {/* 统计信息 */}
          <div className="progress-statistics">
            <Space size="large">
              {estimatedTimeRemaining !== undefined && (
                <Statistic
                  title="预计剩余"
                  value={formatTime(estimatedTimeRemaining)}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ fontSize: 14 }}
                />
              )}

              {averageProcessingTime !== undefined && (
                <Statistic
                  title="平均耗时"
                  value={formatTime(averageProcessingTime)}
                  suffix="/文件"
                  valueStyle={{ fontSize: 14 }}
                />
              )}

              <Statistic
                title="已完成"
                value={currentFileIndex}
                suffix={`/ ${totalFiles}`}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ fontSize: 14 }}
              />
            </Space>
          </div>
        </>
      )}
    </Card>
  );
};